/**
 * @file place.service.js
 * @module PlaceService
 * @description Builds the `from`/`to` address shape used by the experience
 * product form (`address`, `name`, `street`, `postalCode`, `locality`,
 * `region`, `state`, `type`, `country_code`, `location`, `owner`, `place_id`)
 * from either a Google Place Details result or a local (rhombuss-owned)
 * autocomplete option.
 */

const getAddressComponent = (types = [], addressComponents = []) => {
  if (!Array.isArray(types) || !Array.isArray(addressComponents)) {
    return null;
  }

  return (
    addressComponents.find(
      component =>
        Array.isArray(component.types) &&
        component.types.some(type => types.includes(type))
    ) || null
  );
};

/**
 * @param {Object} place - Raw place details (LocationsAPI.getPlaceDetailsByPlaceId
 * response): `location`, `country_code`, `name`, `address_components`, `formatted_address`.
 * @param {Object} point - The selected autocomplete option: `select`, `type`,
 * `owner`, `place_id`.
 */
export const buildLocationFromGooglePlace = (place = {}, point = {}) => {
  const components = place.address_components || [];

  const streetComponent = getAddressComponent(["route"], components);
  const postalCodeComponent = getAddressComponent(["postal_code"], components);
  const localityComponent = getAddressComponent(
    ["locality", "administrative_area_level_3"],
    components
  );
  const regionComponent = getAddressComponent(
    ["administrative_area_level_2", "administrative_area_level_3"],
    components
  );
  const stateComponent = getAddressComponent(
    ["administrative_area_level_1"],
    components
  );
  const countryComponent = getAddressComponent(["country"], components);

  return {
    address: point.select || place.formatted_address || "",
    name: place.name || place.formatted_address || "",
    street: streetComponent ? streetComponent.long_name : "",
    postalCode: postalCodeComponent ? postalCodeComponent.long_name : "",
    locality: localityComponent ? localityComponent.long_name : null,
    region: regionComponent ? regionComponent.long_name : null,
    state: stateComponent ? stateComponent.long_name : null,
    type: point.type || "",
    country_code: countryComponent
      ? countryComponent.short_name
      : place.country_code || "",
    location: place.location || { lat: 0, lng: 0 },
    owner: point.owner || "",
    place_id: point.place_id || null
  };
};

/**
 * @param {Object} point - The selected autocomplete option: `select`, `type`,
 * `owner`, `place_id`, `location`, `country_code`, and (for rhombuss-owned
 * entries such as airports) `metadata.name` / `metadata.city`.
 */
export const buildLocationFromCustomPlace = (point = {}) => {
  const metadata = point.metadata || {};

  return {
    address: point.select || "",
    name: metadata.name || "",
    street: "",
    postalCode: "",
    locality: metadata.city || null,
    region: null,
    state: null,
    type: point.type || "",
    country_code: point.country_code || "",
    location: point.location || { lat: 0, lng: 0 },
    owner: point.owner || "",
    place_id: point.place_id || null
  };
};

/**
 * Icon class for an autocomplete option type.
 * @param {string} type - Google place type of the option.
 * @returns {string}
 */
export const getPlaceIcon = type => {
  if (type === "airport") return "flaticon-bold-plane";
  if (type === "lodging") return "flaticon-bold-bed";
  if (type === "route") return "flaticon-bold-distance";
  if (["train_station", "transit_station"].includes(type)) {
    return "flaticon-bold-train";
  }
  return "flaticon-bold-map";
};

/**
 * OCTO `postalAddress` of a `from`/`to`-shaped address. The country is ISO-2
 * uppercase, as the backend requires.
 * @param {Object} location
 * @returns {Object}
 */
const buildOctoPostalAddress = (location = {}) => ({
  streetAddress: location.address || null,
  addressLocality: location.locality || null,
  addressRegion: location.region || location.state || null,
  postalCode: location.postalCode || null,
  addressCountry: (location.country_code || "").toUpperCase() || null,
  postOfficeBoxNumber: null
});

/**
 * OCTO `identifiers` of a `from`/`to`-shaped address: the Google place id when
 * the address comes from Google.
 * @param {Object} location
 * @returns {Array<{ googlePlaceId: string }>}
 */
const buildOctoIdentifiers = (location = {}) =>
  location.owner === "google" && location.place_id
    ? [{ googlePlaceId: location.place_id }]
    : [];

/**
 * Maps a `from`/`to`-shaped address (output of `buildLocationFromGooglePlace`
 * or `buildLocationFromCustomPlace`) to the OCTO `place` object stored in a
 * product's pickup/dropoff locations.
 * @param {Object} location - `address`, `locality`, `region`, `state`,
 * `postalCode`, `country_code`, `location`, `owner`, `place_id`.
 */
export const buildOctoPlace = (location = {}) => {
  const coordinates = location.location || {};
  return {
    latitude: coordinates.lat || 0,
    longitude: coordinates.lng || 0,
    postalAddress: buildOctoPostalAddress(location),
    identifiers: buildOctoIdentifiers(location),
    sameAs: []
  };
};
